import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Eye, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

export default function InvoiceHistory() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const [activeTab, setActiveTab] = useState("consultas");
  
  // Filtros para notas de consulta
  const [search, setSearch] = useState("");
  const [emitenteFilter, setEmitenteFilter] = useState<number | "ALL">("ALL");
  const [tipoFilter, setTipoFilter] = useState<"entrada" | "saida" | "ALL">("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Modal de detalhes
  const [selectedNota, setSelectedNota] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Modal de reprocessamento
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [notaToReprocess, setNotaToReprocess] = useState<any>(null);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery({});

  // Buscar notas fiscais
  const { data: notas = [], isLoading } = trpc.consultaNf.notas.list.useQuery({
    busca: search || undefined,
    emitenteId: emitenteFilter !== "ALL" ? emitenteFilter : undefined,
    dataInicio: startDate || undefined,
    dataFim: endDate || undefined,
  });

  // Buscar XML da nota selecionada
  const { data: notaDetalhes, isLoading: loadingDetalhes } = trpc.consultaNf.notas.getById.useQuery(
    { id: selectedNota! },
    { enabled: !!selectedNota && modalOpen }
  );

  const utils = trpc.useUtils();
  
  const reprocessMutation = trpc.consultaNf.notas.reprocessar.useMutation({
    onSuccess: () => {
      toast.success('Nota reprocessada com sucesso!');
      utils.consultaNf.notas.list.invalidate();
      setReprocessModalOpen(false);
      setNotaToReprocess(null);
    },
    onError: (error) => {
      toast.error(`Erro ao reprocessar: ${error.message}`);
    },
  });
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  const handleViewNota = (id: number) => {
    setSelectedNota(id);
    setModalOpen(true);
  };
  
  const handleReprocessClick = (nota: any) => {
    setNotaToReprocess(nota);
    setReprocessModalOpen(true);
  };
  
  const handleConfirmReprocess = () => {
    if (notaToReprocess) {
      reprocessMutation.mutate({ id: notaToReprocess.id });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const formatCNPJ = (cnpj: string) => {
    if (!cnpj || cnpj.length !== 14) return cnpj;
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  // Filtrar notas por tipo
  const notasFiltradas = tipoFilter === "ALL" 
    ? notas 
    : notas.filter((n: any) => n.tipoNota === tipoFilter);

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Histórico de Notas Fiscais</h1>
        <p className="text-muted-foreground">
          Gerencie e visualize notas fiscais consultadas e emitidas
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="consultas">Notas de Consulta</TabsTrigger>
          <TabsTrigger value="emitidas">Notas Emitidas</TabsTrigger>
        </TabsList>

        {/* Aba: Notas de Consulta */}
        <TabsContent value="consultas">
          {/* Filtros */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por chave, número..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select
                  value={emitenteFilter === "ALL" ? "ALL" : String(emitenteFilter)}
                  onValueChange={(v) => setEmitenteFilter(v === "ALL" ? "ALL" : Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos os emitentes</SelectItem>
                    {emitentes.map((e: any) => (
                      <SelectItem key={e.id} value={String(e.id)}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={tipoFilter} onValueChange={(v: any) => setTipoFilter(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tomador/Emissor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos</SelectItem>
                    <SelectItem value="entrada">Tomador</SelectItem>
                    <SelectItem value="saida">Emissor</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Data início"
                />

                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="Data fim"
                />
              </div>

              {(search || emitenteFilter !== "ALL" || tipoFilter !== "ALL" || startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch("");
                    setEmitenteFilter("ALL");
                    setTipoFilter("ALL");
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="mt-4"
                >
                  Limpar filtros
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Tabela */}
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Carregando notas fiscais...
                </div>
              ) : notasFiltradas.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma nota fiscal encontrada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Chave NFe</th>
                        <th className="text-left p-4 font-medium">Número</th>
                        <th className="text-left p-4 font-medium">Emitente</th>
                        <th className="text-left p-4 font-medium">Destinatário</th>
                        <th className="text-left p-4 font-medium">Data Emissão</th>
                        <th className="text-right p-4 font-medium">Valor</th>
                        <th className="text-center p-4 font-medium">Tomador/Emissor</th>
                        <th className="text-center p-4 font-medium">Tipo Doc</th>
                        <th className="text-center p-4 font-medium">Status</th>
                        <th className="text-center p-4 font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notasFiltradas.map((nota: any) => (
                        <tr key={nota.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-4 font-mono text-xs">{nota.chaveNfe.substring(0, 20)}...</td>
                          <td className="p-4">{nota.numeroNota || "-"}</td>
                          <td className="p-4">
                            <div className="max-w-[200px]">
                              <div className="font-medium truncate">{nota.nomeEmitente || "-"}</div>
                              <div className="text-xs text-muted-foreground">{formatCNPJ(nota.cnpjEmitente)}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="max-w-[200px]">
                              <div className="font-medium truncate">{nota.nomeDestinatario || "-"}</div>
                              <div className="text-xs text-muted-foreground">{formatCNPJ(nota.cnpjDestinatario)}</div>
                            </div>
                          </td>
                          <td className="p-4">{formatDate(nota.dataEmissao)}</td>
                          <td className="p-4 text-right font-medium">{formatCurrency(nota.valorTotal || 0)}</td>
                          <td className="p-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                nota.tipoNota === "entrada"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {nota.tipoNota === "entrada" ? "Tomador" : "Emissor"}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                              {nota.tipoDocumento || "desconhecido"}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                nota.status === "sucesso"
                                  ? "bg-green-100 text-green-800"
                                  : nota.status === "erro"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                              title={nota.mensagemErro || ""}
                            >
                              {nota.status === "sucesso" ? "Sucesso" : nota.status === "erro" ? "Erro" : "Pendente"}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewNota(nota.id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {(nota.status === "erro" || nota.tipoDocumento === "resumo") && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReprocessClick(nota)}
                                  title="Reprocessar nota"
                                >
                                  <RefreshCw className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba: Notas Emitidas */}
        <TabsContent value="emitidas">
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-semibold mb-2">Em Desenvolvimento</h3>
              <p className="text-muted-foreground">
                A funcionalidade de emissão de notas fiscais estará disponível em breve.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Nota Fiscal</DialogTitle>
          </DialogHeader>

          {loadingDetalhes ? (
            <div className="p-8 text-center text-muted-foreground">
              Carregando detalhes...
            </div>
          ) : notaDetalhes ? (
            <div className="space-y-6">
              {/* Informações Gerais */}
              <div>
                <h3 className="font-semibold mb-3">Informações Gerais</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Chave NFe:</span>
                    <p className="font-mono text-xs mt-1">{notaDetalhes.chaveNfe}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Número:</span>
                    <p className="font-medium mt-1">{notaDetalhes.numeroNota || "-"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Série:</span>
                    <p className="font-medium mt-1">{notaDetalhes.serie || "-"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Data Emissão:</span>
                    <p className="font-medium mt-1">{formatDate(notaDetalhes.dataEmissao)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Valor Total:</span>
                    <p className="font-medium mt-1">{formatCurrency(notaDetalhes.valorTotal || 0)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Tipo:</span>
                    <p className="font-medium mt-1 capitalize">{notaDetalhes.tipoNota || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Emitente */}
              <div>
                <h3 className="font-semibold mb-3">Emitente</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Nome:</span>
                    <p className="font-medium mt-1">{notaDetalhes.nomeEmitente || "-"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">CNPJ:</span>
                    <p className="font-medium mt-1">{formatCNPJ(notaDetalhes.cnpjEmitente)}</p>
                  </div>
                </div>
              </div>

              {/* Destinatário */}
              <div>
                <h3 className="font-semibold mb-3">Destinatário</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Nome:</span>
                    <p className="font-medium mt-1">{notaDetalhes.nomeDestinatario || "-"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">CNPJ:</span>
                    <p className="font-medium mt-1">{formatCNPJ(notaDetalhes.cnpjDestinatario)}</p>
                  </div>
                </div>
              </div>

              {/* Data de Consulta */}
              <div>
                <h3 className="font-semibold mb-3">Informações de Consulta</h3>
                <div className="text-sm">
                  <span className="text-muted-foreground">Data da Consulta:</span>
                  <p className="font-medium mt-1">{formatDate(notaDetalhes.dataPuxada)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              Nota não encontrada
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Reprocessamento */}
      <Dialog open={reprocessModalOpen} onOpenChange={setReprocessModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprocessar Nota Fiscal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Deseja buscar o XML completo desta nota na SEFAZ?</p>
            {notaToReprocess && (
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Chave de Acesso:</p>
                <p className="font-mono text-xs mt-1">{notaToReprocess.chaveNfe}</p>
                {notaToReprocess.mensagemErro && (
                  <div className="mt-2">
                    <p className="text-sm text-muted-foreground">Erro anterior:</p>
                    <p className="text-xs text-red-600 mt-1">{notaToReprocess.mensagemErro}</p>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setReprocessModalOpen(false);
                  setNotaToReprocess(null);
                }}
                disabled={reprocessMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmReprocess}
                disabled={reprocessMutation.isPending}
              >
                {reprocessMutation.isPending ? "Reprocessando..." : "Reprocessar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
